// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend.
  // `window` doesn't exist during SSR (e.g. this function is reached from a duck's loadData,
  // which runs on the server too) - fall back to a relative URL instead of crashing there.
  if (marketplaceRootURL) {
    return marketplaceRootURL.replace(/\/$/, '');
  }
  return typeof window !== 'undefined' ? window.location.origin : '';
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = body ? { body: shouldSerializeBody ? serialize(body) : body } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  // Use the global fetch (not window.fetch) so this also works during SSR (Node 18+ has
  // fetch as a global, but no `window`).
  return fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};


// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};



export const contactMail = body => {
  return post('/api/send-contact-email', body);
};

export const createCollectionData = body => {
  return post('/api/insert-collection', body);
};
export const getCollectionData = body => {
  return post('/api/get-collection', body);
};
export const updateCollectionData = body => {
  return post('/api/updateCollectionData', body);
};

export const deleteCollectionData = body => {
  return post('/api/deleteCollectionData', {
    id: body,
    tableName: 'Invitation_Data',
    fnCallback: false
  });
};

export const onDecryptData = body => {
  return post('/api/decryptData', body);
};
export const singleMailApiCollection = body => {
  return post('/api/send_mail', body);
};

export const deleteImageFromCloudinary = body => {
  return post('/api/delete-images', body);
};
// delete multiple images from cloudinary api
export const deleteMultipleImagesFromCloudinary = body => {
  return post('/api/delete-multi-image-cloudinary', body);
};
export const updateListingData = body => {
  return post('/api/updateListingData', body);
};

export const saveListingAvailabilityData = body => {
  return post('/api/save-listing-availability-data', body);
};

export const getListingAvailabilityData = body => {
  return post('/api/get-listing-availability-data', body);
};

export const saveBookingData = body => {
  return post('/api/save-booking-data', body);
};

export const updateBookingStatus = body => {
  return post('/api/update-booking-status', body);
};

export const getBookingData = body => {
  return post('/api/get-booking-data', body);
};

export const getLinkedBookingData = body => {
  return post('/api/get-linked-booking-data', body);
};

// Algolia
export const createAlgoliaData = body => {
  return post('/api/createAlgoliaData', body);
};

export const updateAlgoliaData = body => {
  return post('/api/updateAlgoliaData', body)
}

export const deleteAlgoliaData = body => {
  return post('/api/deleteAlgoliaData', body);
};

export const queryAlgolia = (body) => {
  return post('/api/query-algolia', body);
}

// Recompute the listing's bookable dates in Algolia (host blocks minus live
// bookings). The booking-event cron does this too; calling it directly just
// makes the change visible in search immediately.
export const syncListingAvailability = body => {
  return post('/api/sync-listing-availability', body);
};
export const showUser = (body) => {
  return post('/api/showUser', body);
}
export const updateMetaData = body => {
  return post('/api/updateMetaData', body);
};
//subscription
export const createSubscription = body => {
  return post('/api/createSubscription', body);
};
export const cancelSubscription = body => {
  return post('/api/cancelSubscription', body);
};
export const payInvoice = body => {
  return post('/api/payInvoice', body);
};
export const cancelStripeSubscription = body => {
  return post('/api/cancelStripeSubscription', body);
};
export const allListSubscription = body => {
  return post('/api/allListSubscription', body);
};

// Certn background-check APIs
export const startIdentityVerification = body => {
  return post('/api/certn/start-verification', body);
};

export const getCaseDetails = body => {
  return post('/api/certn/case-details', body);
};

export const getUserVerificationData = body => {
  return post('/api/certn/user-verification-data', body);
};

export const createSubscriptionWithIdVerification = body => {
  return post('/api/create-subscription', body);
};

export const createFullPaymentSubscription = body => {
  return post('/api/create-subscription-full-payment', body);
};

export const certnWebhook = body => {
  return post('/api/certn/webhook', body);
};
